import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import type { Observable } from 'rxjs';
import { throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { EncryptionService } from './encryption.service';
import { environment } from '../../enviroment/enviroment';
import type { ApiResponse, ErrorResponse } from '../models/api-response';

@Injectable()
export class ApiService {
  private readonly baseUrl = environment.ANGULAR_APP_API_URL;
  private readonly http = inject(HttpClient);
  private readonly encryptionService = inject(EncryptionService);

  isLoggedIn(): boolean {
    const TOKEN = localStorage.getItem('token');
    if (!TOKEN) {
      return false;
    }
    const DECODED = this.encryptionService.decodeBase64(TOKEN);
    return !!DECODED;
  }

  get<T = unknown>(url: string, isPublic = false, hideLoader = false): Observable<ApiResponse<T>> {
    const HEADERS = this.buildHeaders(isPublic, hideLoader);

    return this.http.get<ApiResponse<T>>(this.baseUrl + url, { headers: HEADERS }).pipe(
      map((res) => res),
      catchError((err) => this.handleError(err))
    );
  }

  post<T = unknown>(
    apiSlug: string,
    formData: Record<string, unknown> | FormData,
    isPublic = false,
    hideLoader = false,
    hasFile?: boolean
  ): Observable<ApiResponse<T>> {
    const HEADERS = this.buildHeaders(isPublic, hideLoader);
    const PAYLOAD = this.preparePayload(formData, hasFile);

    return this.http
      .post<ApiResponse<T>>(this.baseUrl + apiSlug, PAYLOAD, { headers: HEADERS })
      .pipe(
        map((res) => res),
        catchError((err) => this.handleError(err))
      );
  }

  put<T = unknown>(
    apiSlug: string,
    formData: Record<string, unknown> | FormData,
    isPublic = false,
    hideLoader = false,
    hasFile?: boolean
  ): Observable<ApiResponse<T>> {
    const HEADERS = this.buildHeaders(isPublic, hideLoader);
    const PAYLOAD = this.preparePayload(formData, hasFile);

    return this.http
      .put<ApiResponse<T>>(this.baseUrl + apiSlug, PAYLOAD, { headers: HEADERS })
      .pipe(
        map((res) => res),
        catchError((err) => this.handleError(err))
      );
  }

  delete<T = unknown>(
    url: string,
    isPublic = false,
    hideLoader = false
  ): Observable<ApiResponse<T>> {
    const HEADERS = this.buildHeaders(isPublic, hideLoader);

    return this.http.delete<ApiResponse<T>>(this.baseUrl + url, { headers: HEADERS }).pipe(
      map((res) => res),
      catchError((err) => this.handleError(err))
    );
  }

  // ----------------------
  // Helpers
  // ----------------------

  private preparePayload(
    formData: Record<string, unknown> | FormData,
    hasFile?: boolean
  ): Record<string, unknown> | FormData {
    if (hasFile && !(formData instanceof FormData)) {
      const FORM_DATA = new FormData();
      Object.keys(formData).forEach((key) => {
        FORM_DATA.append(key, formData[key] as string | Blob);
      });
      return FORM_DATA;
    }
    return formData;
  }

  private buildHeaders(isPublic: boolean, hideLoader: boolean): HttpHeaders {
    let headers = new HttpHeaders();
    if (isPublic) {
      headers = headers.set('isPublic', 'true');
    }
    if (hideLoader) {
      headers = headers.set('hideLoader', 'true');
    }
    return headers;
  }

  private handleError(error: ErrorResponse): Observable<never> {
    if (error.status === 401) {
      this.clearSession();
    }

    // Narrow error.error type safely
    const BACKEND_MSG =
      error.error && typeof error.error === 'object' && 'message' in error.error
        ? (error.error as { message?: string }).message
        : undefined;

    const ERROR_OBJ = {
      statusCode: error.status || 500,
      status: 'Error',
      message:
        BACKEND_MSG ||
        (typeof error.message === 'string' ? error.message : JSON.stringify(error.message)) ||
        'Unexpected error occurred',
      error: error.error,
    };

    return throwError(() => ERROR_OBJ);
  }

  private clearSession(): void {
    localStorage.clear();
    window.location.reload();
  }
}
